import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Divider,
  Tooltip,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  FilePlusIcon,
  MusicNotesIcon,
  GearIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CircleIcon,
  FileTextIcon,
} from '@phosphor-icons/react';
import { styled } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AudioUploadModal from '../../modals/AudioUploadModal';
import AudioUploadConfigurationDrawer from './AudioUploadConfigurationDrawer';
import EvalConfigDrawer from './EvalConfigDrawer';
import { useDashboard } from '../../../hooks/useDashboard';
import {
  getAudioEvaluationFilesApi,
  processCallEvaluationApi,
} from '../../../utils/apis';
import { IAudioEvaluationFile } from '../../../schemas/apis/interface';
import theme from '../../../theme';

// Reusable styled components
const HeaderSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: '12px 24px',
});

interface AudioUploadConfig {
  transcription_service: string;
  GEMINI_TRANSCRIPTION_MODEL?: string;
  OPENAI_TRANSCRIPTION_MODEL?: string;
  GPT_ANALYSIS_MODEL: string;
  recorded_on: string;
  max_workers: string;
  batch_wait_time: string;
  storeName: string;
}

type EvaluationStatus = 'pending' | 'evaluating' | 'success' | 'failed';

// LocalStorage key for persisting evaluating state
const EVALUATING_STORAGE_KEY = 'audio-evaluation-in-progress';

const AudioUpload: React.FC = () => {
  const { selectedAssistant } = useDashboard();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfigDrawerOpen, setIsConfigDrawerOpen] = useState(false);
  const [isEvalConfigDrawerOpen, setIsEvalConfigDrawerOpen] = useState(false);
  const [config, setConfig] = useState<AudioUploadConfig | null>(null);
  const [selectedFileKeys, setSelectedFileKeys] = useState<string[]>([]);
  const [evaluatingKeys, setEvaluatingKeys] = useState<Set<string>>(new Set());
  const [alert, setAlert] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  // Get storage key for current agent
  const getStorageKey = useCallback(() => {
    return `${EVALUATING_STORAGE_KEY}-${selectedAssistant?._id || ''}`;
  }, [selectedAssistant?._id]);

  // Load evaluating keys from localStorage on mount
  useEffect(() => {
    if (!selectedAssistant?._id) return;

    try {
      const stored = localStorage.getItem(getStorageKey());
      if (stored) {
        const parsedKeys = JSON.parse(stored) as string[];
        if (Array.isArray(parsedKeys) && parsedKeys.length > 0) {
          setEvaluatingKeys(new Set(parsedKeys));
          setAlert({
            type: 'info',
            message:
              'Evaluation is still in progress. Status will update when complete.',
          });
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, [selectedAssistant?._id, getStorageKey]);

  // Save evaluating keys to localStorage
  const saveEvaluatingKeys = useCallback(
    (keys: Set<string>) => {
      if (keys.size > 0) {
        localStorage.setItem(getStorageKey(), JSON.stringify([...keys]));
      } else {
        localStorage.removeItem(getStorageKey());
      }
    },
    [getStorageKey]
  );

  // Clear evaluating keys from localStorage
  const clearEvaluatingKeys = useCallback(() => {
    localStorage.removeItem(getStorageKey());
    setEvaluatingKeys(new Set());
  }, [getStorageKey]);

  // Fetch audio files - poll every 10 seconds if there are evaluating files
  const { data: audioFilesData, isLoading } = useQuery({
    queryKey: [
      'audio-evaluation-files',
      { agentId: selectedAssistant?._id || '' },
    ] as [string, { agentId: string }],
    queryFn: getAudioEvaluationFilesApi,
    enabled: !!selectedAssistant?._id,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 1000 * 60 * 1,
    gcTime: 1000 * 60 * 10,
    refetchInterval: evaluatingKeys.size > 0 ? 10000 : false, // Poll every 10s when evaluating
  });

  // Check if evaluating files have completed and clear them
  useEffect(() => {
    if (evaluatingKeys.size === 0 || !audioFilesData?.data?.data?.files) return;

    const apiFiles = audioFilesData.data.data.files as IAudioEvaluationFile[];
    const completedKeys: string[] = [];

    evaluatingKeys.forEach(key => {
      const file = apiFiles.find(f => f.s3Key === key);
      // If file has a final status (success or failed), it's completed
      if (
        file &&
        (file.evaluationStatus === 'success' ||
          file.evaluationStatus === 'failed')
      ) {
        completedKeys.push(key);
      }
    });

    if (completedKeys.length > 0) {
      const remainingKeys = new Set(
        [...evaluatingKeys].filter(k => !completedKeys.includes(k))
      );
      setEvaluatingKeys(remainingKeys);
      saveEvaluatingKeys(remainingKeys);

      if (remainingKeys.size === 0) {
        setAlert({
          type: 'success',
          message: 'Evaluation completed! Check the status of each file.',
        });
        setTimeout(() => setAlert(null), 5000);
      }
    }
  }, [audioFilesData, evaluatingKeys, saveEvaluatingKeys]);

  const audioFiles: IAudioEvaluationFile[] = useMemo(() => {
    if (
      !audioFilesData ||
      !audioFilesData.data ||
      !audioFilesData.data.data ||
      !audioFilesData.data.data.files
    )
      return [];
    return audioFilesData.data.data.files.map((file: IAudioEvaluationFile) => ({
      ...file,
      // Override with 'evaluating' if currently being evaluated
      evaluationStatus: evaluatingKeys.has(file.s3Key)
        ? 'evaluating'
        : file.evaluationStatus || 'pending',
    }));
  }, [audioFilesData, evaluatingKeys]);

  // Evaluate files mutation
  const { mutate: evaluateFiles, isPending: isEvaluating } = useMutation({
    mutationFn: processCallEvaluationApi,
    onMutate: () => {
      // Set selected files to 'evaluating' and persist to localStorage
      const newKeys = new Set(selectedFileKeys);
      setEvaluatingKeys(newKeys);
      saveEvaluatingKeys(newKeys);
      setAlert({
        type: 'info',
        message:
          'Evaluation started! We will update the status once it completes.',
      });
    },
    onSuccess: () => {
      // Clear evaluating keys from state and localStorage, then refetch
      clearEvaluatingKeys();
      queryClient.invalidateQueries({
        queryKey: ['audio-evaluation-files'],
      });
      setAlert({
        type: 'success',
        message: 'Evaluation completed! Check the status of each file.',
      });
      setSelectedFileKeys([]);
      setTimeout(() => setAlert(null), 5000);
    },
    onError: (error: any) => {
      // Clear evaluating keys from state and localStorage, then refetch
      clearEvaluatingKeys();
      queryClient.invalidateQueries({
        queryKey: ['audio-evaluation-files'],
      });
      setAlert({
        type: 'error',
        message:
          error?.response?.data?.message ||
          'Evaluation failed. Please check the status of each file.',
      });
      setTimeout(() => setAlert(null), 5000);
    },
  });

  const handleConfigSave = (newConfig: AudioUploadConfig) => {
    setConfig(newConfig);
  };

  // Get store name from assistant's knowledge base stores
  const getStoreName = (): string => {
    if (
      selectedAssistant?.config?.knowledgeBase &&
      selectedAssistant.config.knowledgeBase.length > 0
    ) {
      return (
        selectedAssistant.config.knowledgeBase[0].store?.friendly_name ||
        'test-store'
      );
    }
    return 'test-store'; // Default fallback
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get files that can be selected (not already evaluated)
  const selectableFiles = useMemo(
    () => audioFiles.filter(file => file.evaluationStatus !== 'success'),
    [audioFiles]
  );

  const handleSelectAll = () => {
    if (selectedFileKeys.length === selectableFiles.length) {
      setSelectedFileKeys([]);
    } else {
      setSelectedFileKeys(selectableFiles.map(file => file.s3Key));
    }
  };

  const handleSelectFile = (s3Key: string, isEvaluated: boolean) => {
    if (isEvaluated) return; // Don't allow selecting evaluated files
    setSelectedFileKeys(prev =>
      prev.includes(s3Key)
        ? prev.filter(key => key !== s3Key)
        : [...prev, s3Key]
    );
  };

  const handleEvaluate = async () => {
    if (!config) {
      setAlert({
        type: 'error',
        message:
          'Please configure the evaluation settings first by clicking the gear icon.',
      });
      return;
    }

    if (!config.storeName) {
      setAlert({
        type: 'error',
        message: 'Please select a store in the configuration settings.',
      });
      return;
    }

    if (selectedFileKeys.length === 0) {
      setAlert({
        type: 'error',
        message: 'Please select at least one file to evaluate.',
      });
      return;
    }

    // Get selected files
    const selectedFiles = audioFiles.filter(file =>
      selectedFileKeys.includes(file.s3Key)
    );

    // Convert S3 URLs to File objects by fetching them
    try {
      const filePromises = selectedFiles.map(async file => {
        const response = await fetch(file.s3Url);
        const blob = await response.blob();
        return new File([blob], file.fileName, { type: 'audio/wav' });
      });

      const files = await Promise.all(filePromises);

      // Prepare API payload
      const apiPayload: any = {
        agentId: selectedAssistant?._id || '',
        storeName: config.storeName || getStoreName(), // Use config store or fallback
        transcription_service: config.transcription_service,
        GPT_ANALYSIS_MODEL: config.GPT_ANALYSIS_MODEL,
        recorded_on: config.recorded_on,
        max_workers: config.max_workers,
        batch_wait_time: config.batch_wait_time,
        audioFiles: files,
      };

      // Add the correct transcription model field based on service
      if (config.transcription_service === 'gemini') {
        apiPayload.GEMINI_TRANSCRIPTION_MODEL =
          config.GEMINI_TRANSCRIPTION_MODEL || 'gemini-2.5-flash';
      } else if (config.transcription_service === 'openai') {
        apiPayload.OPENAI_TRANSCRIPTION_MODEL =
          config.OPENAI_TRANSCRIPTION_MODEL || 'gpt-4o-transcribe-diarize';
      }

      evaluateFiles(apiPayload);
    } catch {
      setAlert({
        type: 'error',
        message: 'Failed to fetch audio files. Please try again.',
      });
    }
  };

  const getStatusIcon = (status: EvaluationStatus) => {
    switch (status) {
      case 'evaluating':
        return <ClockIcon size={16} weight="bold" />;
      case 'success':
        return <CheckCircleIcon size={16} weight="bold" />;
      case 'failed':
        return <XCircleIcon size={16} weight="bold" />;
      default:
        return <CircleIcon size={16} weight="bold" />;
    }
  };

  const getStatusColor = (status: EvaluationStatus) => {
    switch (status) {
      case 'evaluating':
        return '#3B82F6';
      case 'success':
        return '#10B981';
      case 'failed':
        return '#EF4444';
      default:
        return '#9CA3AF';
    }
  };

  const getStatusLabel = (status: EvaluationStatus) => {
    switch (status) {
      case 'evaluating':
        return 'Evaluating';
      case 'success':
        return 'Evaluated';
      case 'failed':
        return 'Failed';
      default:
        return 'Pending';
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {alert && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert(null)}
          sx={{ mx: 2, mt: 2, flexShrink: 0 }}
        >
          {alert.message}
        </Alert>
      )}

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      ) : audioFiles.length > 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Fixed Header */}
          <Box
            sx={{
              flexShrink: 0,
              backgroundColor: 'white',
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <HeaderSection>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography fontWeight={600} fontSize={16}>
                    Audio Files
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {audioFiles.length} file(s)
                    {selectedFileKeys.length > 0 &&
                      ` • ${selectedFileKeys.length} selected`}
                  </Typography>
                </Box>
                {selectableFiles.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={
                        selectableFiles.length > 0 &&
                        selectedFileKeys.length === selectableFiles.length
                      }
                      indeterminate={
                        selectedFileKeys.length > 0 &&
                        selectedFileKeys.length < selectableFiles.length
                      }
                      onChange={handleSelectAll}
                      sx={{ p: 0 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Select All
                    </Typography>
                  </Box>
                )}
              </HeaderSection>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  marginRight: '16px',
                }}
              >
                {selectedFileKeys.length > 0 && (
                  <Tooltip
                    title={
                      !config
                        ? 'Please configure evaluation settings first (click the gear icon)'
                        : ''
                    }
                    arrow
                    placement="bottom"
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleEvaluate}
                        disabled={isEvaluating || !config}
                        startIcon={
                          isEvaluating ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : null
                        }
                        sx={{ textTransform: 'none' }}
                      >
                        {isEvaluating
                          ? 'Evaluating...'
                          : `Evaluatee ${selectedFileKeys.length} File(s)`}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Tooltip
                  title="Evaluation Prompts Configuration"
                  arrow
                  placement="bottom"
                >
                  <Box
                    onClick={() => setIsEvalConfigDrawerOpen(true)}
                    sx={{
                      color: theme.palette.text.secondary,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': {
                        color: theme.palette.primary.main,
                      },
                      transition: 'color 0.2s ease',
                    }}
                  >
                    <FileTextIcon size={24} />
                  </Box>
                </Tooltip>
                <Tooltip
                  title={
                    !config && selectedFileKeys.length > 0
                      ? 'Click to configure evaluation settings (required)'
                      : 'Configuration'
                  }
                  arrow
                >
                  <Box
                    onClick={() => setIsConfigDrawerOpen(true)}
                    sx={{
                      color:
                        !config && selectedFileKeys.length > 0
                          ? theme.palette.warning.main
                          : theme.palette.text.secondary,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                      animation:
                        !config && selectedFileKeys.length > 0
                          ? 'pulse 1.5s infinite'
                          : 'none',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.15)' },
                        '100%': { transform: 'scale(1)' },
                      },
                    }}
                  >
                    <GearIcon size={24} />
                    {!config && selectedFileKeys.length > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.warning.main,
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>
                <FilePlusIcon
                  size={28}
                  style={{
                    color: theme.palette.primary.main,
                    cursor: 'pointer',
                  }}
                  onClick={() => setIsModalOpen(true)}
                />
              </Box>
            </Box>
            <Divider />
          </Box>

          {/* Scrollable Files Section */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <Stack spacing={2} sx={{ p: 2 }}>
              {audioFiles.map(file => {
                const isEvaluated = file.evaluationStatus === 'success';
                const status = file.evaluationStatus || 'pending';
                return (
                  <Paper
                    key={file.s3Key}
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      background: isEvaluated ? '#F9FAFB' : '#FFFFFF',
                      border: `2px solid ${
                        selectedFileKeys.includes(file.s3Key)
                          ? theme.palette.primary.main
                          : '#E5E7EB'
                      }`,
                      boxShadow: 'none',
                      cursor: isEvaluated ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box',
                      width: '100%',
                      opacity: isEvaluated ? 0.7 : 1,
                      '&:hover': isEvaluated
                        ? {}
                        : {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: '#F9FAFB',
                          },
                    }}
                    onClick={() => handleSelectFile(file.s3Key, isEvaluated)}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <Checkbox
                          checked={selectedFileKeys.includes(file.s3Key)}
                          onChange={() =>
                            handleSelectFile(file.s3Key, isEvaluated)
                          }
                          onClick={e => e.stopPropagation()}
                          disabled={isEvaluated}
                          sx={{ flexShrink: 0 }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 500,
                              color: isEvaluated ? '#9CA3AF' : '#111827',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {file.fileName}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isEvaluated ? '#9CA3AF' : '#6B7280',
                              fontSize: '14px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Uploaded on {formatDate(file.lastModified)} •{' '}
                            {formatFileSize(file.size)}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        icon={getStatusIcon(status)}
                        label={getStatusLabel(status)}
                        size="small"
                        sx={{
                          flexShrink: 0,
                          backgroundColor:
                            status === 'evaluating'
                              ? 'rgba(59, 130, 246, 0.1)'
                              : status === 'success'
                                ? 'rgba(16, 185, 129, 0.1)'
                                : status === 'failed'
                                  ? 'rgba(239, 68, 68, 0.1)'
                                  : 'rgba(156, 163, 175, 0.1)',
                          color: getStatusColor(status),
                          fontWeight: 500,
                          '& .MuiChip-icon': {
                            color: getStatusColor(status),
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <HeaderSection>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>
                  Audio Files
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage your uploaded audio files
                </Typography>
              </HeaderSection>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  marginRight: '16px',
                }}
              >
                <Tooltip
                  title="Evaluation Prompts Configuration"
                  arrow
                  placement="bottom"
                >
                  <Box
                    onClick={() => setIsEvalConfigDrawerOpen(true)}
                    sx={{
                      color: theme.palette.text.secondary,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': {
                        color: theme.palette.primary.main,
                      },
                      transition: 'color 0.2s ease',
                    }}
                  >
                    <FileTextIcon size={24} />
                  </Box>
                </Tooltip>
                <Tooltip title="Configuration">
                  <Box
                    onClick={() => setIsConfigDrawerOpen(true)}
                    sx={{
                      color: theme.palette.text.secondary,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <GearIcon size={24} />
                  </Box>
                </Tooltip>
              </Box>
            </Box>
            <Divider />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: 2,
                border: '1px dashed #DCE0E7',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
              }}
            >
              <MusicNotesIcon size={30} color={theme.palette.lightGray.text} />
              <Typography variant="body1" color="text.secondary">
                Please upload audio files to get started.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supported formats: MP3, WAV, M4A, OGG, WebM
              </Typography>
              <Button
                variant="contained"
                startIcon={<MusicNotesIcon size={20} />}
                onClick={() => setIsModalOpen(true)}
                sx={{ textTransform: 'none', mt: 2 }}
              >
                Upload Audio Files
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      <AudioUploadModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        agentId={selectedAssistant?._id || ''}
      />
      <AudioUploadConfigurationDrawer
        open={isConfigDrawerOpen}
        onClose={() => setIsConfigDrawerOpen(false)}
        onSave={handleConfigSave}
        initialConfig={config}
      />
      <EvalConfigDrawer
        open={isEvalConfigDrawerOpen}
        onClose={() => setIsEvalConfigDrawerOpen(false)}
      />
    </Box>
  );
};

export default AudioUpload;
